import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageType, COMPONENT_LIBRARY, ComponentTemplate } from '@/types/sitemap';
import { Search, Plus, FileText, Layout, Layers, ChevronDown, ChevronRight, ArrowLeft, PanelLeftClose } from 'lucide-react';
import { Input } from '@/components/ui/input';

const pageTemplates: { type: PageType; label: string }[] = [
  { type: 'home', label: 'Homepage' },
  { type: 'landing', label: 'Landing Page' },
  { type: 'blog', label: 'Blog' },
  { type: 'blog-archive', label: 'Blog Archive' },
  { type: 'blog-single', label: 'Blog Post' },
  { type: 'about', label: 'About' },
  { type: 'contact', label: 'Contact' },
  { type: 'pricing', label: 'Pricing' },
  { type: 'product', label: 'Product' },
  { type: 'login', label: 'Login' },
  { type: 'signup', label: 'Sign Up' },
  { type: 'dashboard', label: 'Dashboard' },
  { type: 'profile', label: 'Profile' },
  { type: 'settings', label: 'Settings' },
  { type: 'portfolio', label: 'Portfolio' },
  { type: 'services', label: 'Services' },
  { type: 'careers', label: 'Careers' },
  { type: 'faq', label: 'FAQ' },
  { type: 'terms', label: 'Terms & Conditions' },
  { type: 'privacy', label: 'Privacy Policy' },
  { type: '404', label: '404 Page' },
  { type: 'default', label: 'Custom Page' },
];

const categoryLabels: Record<string, string> = {
  navigation: 'Navigation',
  hero: 'Hero Sections',
  content: 'Content',
  'social-proof': 'Social Proof',
  conversion: 'Conversion',
  footer: 'Footer',
  utility: 'Utility',
};

const categoryIcons: Record<string, React.ReactNode> = {
  navigation: <Layout size={13} />,
  hero: <Layers size={13} />,
  content: <FileText size={13} />,
  'social-proof': <FileText size={13} />,
  conversion: <FileText size={13} />,
  footer: <Layout size={13} />,
  utility: <FileText size={13} />,
};

interface Props {
  blueprintName?: string;
  onAddPage: (type: PageType, name: string) => void;
  onAddSectionToPage: (sectionName: string) => void;
  pageCount: number;
  selectedPageId: string | null;
  onToggleSidebar: () => void;
  backPath?: string;
}

export function Sidebar({ blueprintName, onAddPage, onAddSectionToPage, pageCount, selectedPageId, onToggleSidebar, backPath = '/brand/sitemaps' }: Props) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'pages' | 'components'>('pages');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['hero', 'content', 'conversion']));
  const [customPageName, setCustomPageName] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const filteredPages = useMemo(() => {
    if (!search) return pageTemplates;
    return pageTemplates.filter(p => p.label.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  const filteredComponents = useMemo(() => {
    if (!search) return COMPONENT_LIBRARY;
    return COMPONENT_LIBRARY.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  const groupedComponents = useMemo(() => {
    const groups: Record<string, ComponentTemplate[]> = {};
    for (const c of filteredComponents) {
      if (!groups[c.category]) groups[c.category] = [];
      groups[c.category].push(c);
    }
    return groups;
  }, [filteredComponents]);

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const handleAddPage = (type: PageType, label: string) => {
    if (type === 'default') {
      setShowCustomInput(true);
      return;
    }
    onAddPage(type, label);
  };

  const handleCustomAdd = () => {
    if (customPageName.trim()) {
      onAddPage('default', customPageName.trim());
      setCustomPageName('');
      setShowCustomInput(false);
    }
  };

  return (
    <div className="w-72 bg-white dark:bg-sidebar border-r border-gray-200 dark:border-sidebar-border flex flex-col h-full">
      {/* Header: Back button + Logo + Collapse toggle */}
      <div className="px-3 py-3 border-b border-gray-200 dark:border-sidebar-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(backPath)}
            className="p-2 rounded-lg text-sidebar-muted hover:bg-gray-100 dark:hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft size={18} />
          </button>
          <span className="text-sm font-semibold text-sidebar-foreground tracking-tight truncate max-w-[160px]" title={blueprintName || 'Sitemap Builder'}>
            {blueprintName || 'Sitemap Builder'}
          </span>
        </div>
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg text-sidebar-muted hover:bg-gray-100 dark:hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
          title="Collapse sidebar"
        >
          <PanelLeftClose size={18} />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 pt-4 pb-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sidebar-muted" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search pages & components..."
            className="h-8 pl-9 text-xs bg-gray-50 dark:bg-sidebar-accent border-gray-200 dark:border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-muted focus-visible:bg-white dark:focus-visible:bg-sidebar-accent transition-colors"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 flex gap-1 mb-1">
        <button
          onClick={() => setTab('pages')}
          className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${
            tab === 'pages'
              ? 'bg-sidebar-primary text-sidebar-primary-foreground'
              : 'text-sidebar-muted hover:text-sidebar-foreground hover:bg-gray-100 dark:hover:bg-sidebar-accent'
          }`}
        >
          Pages
        </button>
        <button
          onClick={() => setTab('components')}
          className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${
            tab === 'components'
              ? 'bg-sidebar-primary text-sidebar-primary-foreground'
              : 'text-sidebar-muted hover:text-sidebar-foreground hover:bg-gray-100 dark:hover:bg-sidebar-accent'
          }`}
        >
          Components
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-2 scrollbar-none">
        {tab === 'pages' ? (
          <div className="space-y-1">
            {filteredPages.map(template => (
              <button
                key={template.type}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/sitemapper-page', JSON.stringify({ type: template.type, label: template.label }));
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => handleAddPage(template.type, template.label)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-sidebar-foreground hover:bg-gray-100 dark:hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group cursor-grab active:cursor-grabbing"
              >
                <span className="w-2 h-2 rounded-full bg-sidebar-muted group-hover:bg-sidebar-primary transition-colors" />
                {template.label}
                <Plus size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-sidebar-muted" />
              </button>
            ))}

            {showCustomInput && (
              <div className="mt-2 flex gap-1.5">
                <Input
                  value={customPageName}
                  onChange={e => setCustomPageName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCustomAdd()}
                  placeholder="Page name..."
                  className="h-8 text-xs bg-gray-50 dark:bg-sidebar-accent border-gray-200 dark:border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-muted"
                  autoFocus
                />
                <button
                  onClick={handleCustomAdd}
                  className="h-8 px-3 text-xs rounded-md bg-sidebar-primary text-sidebar-primary-foreground hover:opacity-90 transition-opacity"
                >
                  Add
                </button>
              </div>
            )}

            {filteredPages.length === 0 && (
              <p className="text-xs text-sidebar-muted text-center py-4">No pages match "{search}"</p>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {!selectedPageId && (
              <div className="bg-gray-50 dark:bg-sidebar-accent/50 border border-gray-200/60 dark:border-transparent rounded-lg px-3 py-2.5 mb-3">
                <p className="text-[11px] text-sidebar-muted leading-relaxed">
                  Select a page on the canvas first, then add components to it.
                </p>
              </div>
            )}
            {Object.entries(groupedComponents).map(([category, components]) => (
              <div key={category}>
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-[11px] font-semibold text-sidebar-foreground uppercase tracking-wider hover:bg-gray-100 dark:hover:bg-sidebar-accent/60 rounded-md hover:text-sidebar-accent-foreground transition-colors"
                >
                  {expandedCategories.has(category) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {categoryIcons[category]}
                  {categoryLabels[category] || category}
                  <span className="ml-auto text-[10px] font-normal">{components.length}</span>
                </button>
                {expandedCategories.has(category) && (
                  <div className="ml-2 space-y-0.5 mb-2">
                    {components.map(comp => (
                      <button
                        key={comp.name}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('application/sitemapper-component', comp.name);
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        onClick={() => selectedPageId && onAddSectionToPage(comp.name)}
                        disabled={!selectedPageId}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-sidebar-foreground hover:bg-gray-100 dark:hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors disabled:opacity-60 disabled:cursor-not-allowed group cursor-grab active:cursor-grabbing"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-sidebar-foreground/50 group-hover:bg-sidebar-primary transition-colors" />
                        {comp.name}
                        <Plus size={12} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-sidebar-muted" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {Object.keys(groupedComponents).length === 0 && (
              <p className="text-xs text-sidebar-muted text-center py-4">No components match "{search}"</p>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="px-5 py-3 border-t border-gray-200 dark:border-sidebar-border">
        <div className="flex items-center justify-between text-xs text-sidebar-muted">
          <span>{pageCount} page{pageCount !== 1 ? 's' : ''}</span>
          <span className="text-[10px]">Powered by AgencyPlus</span>
        </div>
      </div>
    </div>
  );
}
